import React from 'react';
import ReactDOM from 'react-dom';
import '../css/JubileeDetailModal.css';

const JubileeDetailModal = ({ isOpen, onClose, jubilee }) => {
  if (!isOpen || !jubilee) return null;

  // Format date to Dutch format with weekday
  const formatFullDate = (date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calculate years and months duration
  const calculateDuration = (startDate) => {
    const today = new Date();
    const moveInDate = new Date(startDate);
    
    // Calculate years
    let years = today.getFullYear() - moveInDate.getFullYear();
    
    // Adjust if we haven't reached the anniversary month/day yet this year
    if (
      today.getMonth() < moveInDate.getMonth() || 
      (today.getMonth() === moveInDate.getMonth() && today.getDate() < moveInDate.getDate())
    ) {
      years--;
    }
    
    // Calculate months
    let months = today.getMonth() - moveInDate.getMonth();
    if (months < 0) months += 12;
    if (today.getDate() < moveInDate.getDate()) {
      months = (months === 0) ? 11 : months - 1;
    }
    
    return { years, months };
  };

  // Calculate original move-in date from jubilee date
  const getMoveInDate = () => {
    const jubileeDate = new Date(jubilee.jubileeDate);
    const jubileeYears = jubilee.jubileeType;
    
    const moveInDate = new Date(jubileeDate);
    
    // For 12.5 years, subtract 12 years and 6 months
    if (jubileeYears === 12.5) {
      moveInDate.setFullYear(jubileeDate.getFullYear() - 12);
      moveInDate.setMonth(jubileeDate.getMonth() - 6);
    } else {
      moveInDate.setFullYear(jubileeDate.getFullYear() - jubileeYears);
    }
    
    return moveInDate;
  };

  const moveInDate = getMoveInDate();
  const { years, months } = calculateDuration(moveInDate);
  
  // Get jubilee type name
  const getJubileeTypeName = (years) => {
    switch(years) {
      case 12.5: return "Koperen";
      case 25: return "Zilveren";
      case 40: return "Robijnen";
      case 50: return "Gouden";
      case 60: return "Diamanten";
      default: return "";
    }
  };

  // Add to Calendar function
  const addToCalendar = () => {
    const jubileeDate = new Date(jubilee.jubileeDate);
    const title = `${getJubileeTypeName(jubilee.jubileeType)} jubileum ${jubilee.naam}`;
    const dateString = jubileeDate.toISOString().replace(/-|:|\.\d+/g, '');
    const endDate = new Date(jubileeDate);
    endDate.setHours(jubileeDate.getHours() + 1);
    const endDateString = endDate.toISOString().replace(/-|:|\.\d+/g, '');
    
    const href = `data:text/calendar;charset=utf8,BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
URL:${document.URL}
DTSTART:${dateString}
DTEND:${endDateString}
SUMMARY:${title}
DESCRIPTION:${jubilee.jubileeType} jaar jubileum voor ${jubilee.naam}, appartement ${jubilee.nummer}
LOCATION:Appartement ${jubilee.nummer}
END:VEVENT
END:VCALENDAR`;
    
    const link = document.createElement('a');
    link.href = encodeURI(href);
    link.setAttribute('download', `jubileum-${jubilee.naam}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Background color based on jubilee type
  const getBadgeColor = () => {
    switch(jubilee.jubileeType) {
      case 12.5: return "#0066cc"; // Copper-like blue
      case 25: return "#647687";   // Silver-like
      case 40: return "#b7410e";   // Ruby-like
      case 50: return "#e6c200";   // Gold-like
      case 60: return "#a2c4d9";   // Diamond-like
      default: return "#0066cc";
    }
  };

  // Get celebration text based on jubilee type
  const getCelebrationText = (jubileeType) => {
    switch(jubileeType) {
      case 12.5:
        return "Traditioneel het koperen jubileum. Dit koperen jubileum is een mooie gelegenheid voor een felicitatie.";
      case 25:
        return "Een kwart eeuw in ons gebouw! Dit zilveren jubileum is een mooie gelegenheid voor een felicitatie.";
      case 40:
        return "Een bijzonder robijnen jubileum dat zeker aandacht verdient.";
      case 50:
        return "Een halve eeuw in ons gebouw! Dit gouden jubileum verdient speciale aandacht.";
      case 60:
        return "Een uitzonderlijk diamanten jubileum.";
      default:
        return "Een prachtige mijlpaal om te vieren!";
    }
  };

  return ReactDOM.createPortal(
    <div className="jubilee-detail-modal-overlay" onClick={(e) => {
      if (e.target.className === 'jubilee-detail-modal-overlay') onClose();
    }}>
      <div className="jubilee-detail-modal">
        {/* Header */}
        <div className="jubilee-detail-header">
          <h2>{getJubileeTypeName(jubilee.jubileeType)} Jubileum</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        {/* Main content */}
        <div className="jubilee-detail-content">
          {/* Hero section with names and badge */}
          <div className="hero-section">
            <div className="year-badge-container">
              <div className="year-badge" style={{ backgroundColor: getBadgeColor() }}>
                {jubilee.jubileeType === 12.5 ? '12½' : jubilee.jubileeType}
              </div>
              <div className="year-label">jaar</div>
            </div>
            
            <div className="resident-info">
              <h3>{jubilee.naam}</h3>
              <p>Appartement #{jubilee.nummer}</p>
            </div>
          </div>
          
          {/* Countdown circle */}
          <div className="countdown-container">
            <div className="countdown-circle">
              <div className="countdown-number">{jubilee.daysUntil}</div>
              <div className="countdown-text">dagen</div>
            </div>
            <div className="countdown-label">Tot het jubileum</div>
          </div>
          
          {/* Information table */}
          <div className="info-table">
            <div className="info-row">
              <div className="info-label">Woont hier sinds</div>
              <div className="info-value">{formatFullDate(moveInDate)}</div>
            </div>
            <div className="info-row">
              <div className="info-label">Jubileumdatum</div>
              <div className="info-value highlight">{formatFullDate(new Date(jubilee.jubileeDate))}</div>
            </div>
            <div className="info-row">
              <div className="info-label">Huidige woonduur</div>
              <div className="info-value">
                {years > 0 ? `${years} jaar` : ""} 
                {months > 0 ? `${years > 0 ? " en " : ""}${months} ${months === 1 ? "maand" : "maanden"}` : ""}
              </div>
            </div>
          </div>
          
          {/* Celebration text */}
          <div className="celebration-box">
            <div className="celebration-title">
              {jubilee.jubileeType === 12.5 ? '12½' : jubilee.jubileeType} jaar - {getJubileeTypeName(jubilee.jubileeType)} jubileum
            </div>
            <p>{getCelebrationText(jubilee.jubileeType)}</p>
          </div>
          
          {/* Action buttons */}
          <div className="action-buttons">
            <button className="calendar-button" onClick={addToCalendar}>
              <span className="button-icon">📅</span> Toevoegen aan kalender
            </button>
            <button className="close-action" onClick={onClose}>Sluiten</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JubileeDetailModal;